"use client"

import { useState, useEffect } from "react"
import { getConsent, setConsent, hasConsented, type ConsentState } from "@/lib/consent"

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [prefs, setPrefs] = useState<Omit<ConsentState, 'necessary'>>({
    functional: true,
    analytics: true,
    marketing: false,
  })

  useEffect(() => {
    // Hide banner if already consented
    if (!hasConsented()) {
      setIsVisible(true)
    }

    // Initialize prefs from cookie if available
    const current = getConsent()
    if (current) {
      setPrefs({
        functional: current.functional,
        analytics: current.analytics,
        marketing: current.marketing,
      })
    }

    // Listen for manual modal triggers
    const handleOpenModal = () => setShowModal(true)
    window.addEventListener("openPrivacyModal", handleOpenModal)
    return () => window.removeEventListener("openPrivacyModal", handleOpenModal)
  }, [])

  const dispatchUpdate = () => {
    window.dispatchEvent(new CustomEvent("consentUpdated"))
  }

  const handleAcceptAll = () => {
    const allIn = {
      functional: true,
      analytics: true,
      marketing: true,
    }
    setConsent(allIn)
    setPrefs(allIn)
    setIsVisible(false)
    setShowModal(false)
    dispatchUpdate()
  }

  const handleSavePreferences = () => {
    setConsent(prefs)
    setIsVisible(false)
    setShowModal(false)
    dispatchUpdate()
  }

  if (!isVisible && !showModal) return null

  return (
    <>
      {/* Banner */}
      {isVisible && !showModal && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] animate-in fade-in slide-in-from-bottom-5 duration-500">
          <div className="bg-bloom-green-deep border-t border-white/10 px-6 py-4">
            <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
                <button 
                  onClick={() => setShowModal(true)}
                  className="text-white hover:text-bloom-gold font-sans font-bold text-[11px] uppercase tracking-wider transition-colors focus-bloom-light rounded py-1 px-2"
                >
                  Preferences
                </button>
                <p className="hidden md:block font-sans text-xs text-white/70 max-w-sm leading-snug">
                  We use cookies to analyze traffic and improve your experience. 
                  View our <a href="/cookies" className="underline hover:text-white">Cookie Policy</a> for details.
                </p>
              </div>
              
              <div className="flex items-center gap-3 w-full md:w-auto">
                <button 
                  onClick={() => {
                    const none = { functional: false, analytics: false, marketing: false }
                    setConsent(none)
                    setPrefs(none)
                    setIsVisible(false)
                    dispatchUpdate()
                  }}
                  className="flex-1 md:flex-none bg-bloom-cream text-bloom-charcoal border border-transparent font-sans font-bold text-sm py-2.5 px-6 rounded-full hover:bg-white hover:text-bloom-charcoal transition-all focus-bloom-light active:scale-95"
                >
                  Necessary Only
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 md:flex-none bg-bloom-gold text-bloom-charcoal font-sans font-bold text-sm py-2.5 px-6 rounded-full hover:bg-bloom-gold-dark transition-all shadow-lg focus-bloom-light active:scale-95"
                >
                  Allow All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-bloom-charcoal/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-md rounded-[32px] p-8 shadow-2xl border border-bloom-border">
            <h2 className="font-serif text-2xl font-normal text-bloom-charcoal mb-6">Privacy Preferences</h2>
            
            <div className="space-y-6">
              {[
                { 
                  id: "necessary", 
                  label: "Strictly Necessary", 
                  description: "Required for basic site functionality. Cannot be disabled.",
                  checked: true,
                  readonly: true 
                },
                { 
                  id: "functional", 
                  label: "Functional", 
                  description: "Enables core widgets like the discovery call booking system.",
                  checked: prefs.functional,
                  readonly: false 
                },
                { 
                  id: "analytics", 
                  label: "Analytics", 
                  description: "Helps us understand how visitors interact with our content.",
                  checked: prefs.analytics,
                  readonly: false 
                },
                { 
                  id: "marketing", 
                  label: "Marketing", 
                  description: "Used to deliver relevant announcements and organizational updates.",
                  checked: prefs.marketing,
                  readonly: false 
                },
              ].map((item) => (
                <div key={item.id} className="flex items-start justify-between gap-4 py-4 border-b border-bloom-border/50 last:border-0 hover:bg-bloom-cream/20 transition-colors p-2 rounded-xl">
                  <div className="flex-1">
                    <p className="font-sans font-bold text-sm text-bloom-charcoal">{item.label}</p>
                    <p className="font-sans text-xs text-bloom-muted mt-1 leading-relaxed">{item.description}</p>
                  </div>
                  <div className="flex items-center pt-1">
                    <input
                      type="checkbox"
                      id={`cookie-${item.id}`}
                      checked={item.checked}
                      disabled={item.readonly}
                      onChange={(e) => !item.readonly && setPrefs(prev => ({ ...prev, [item.id]: e.target.checked }))}
                      className="w-5 h-5 rounded border-bloom-border text-bloom-green-deep focus:ring-bloom-green-mid"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 mt-10">
              <button 
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-3 font-sans font-bold text-sm text-bloom-muted hover:text-bloom-charcoal transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSavePreferences}
                className="flex-2 px-8 py-3 bg-bloom-green-deep text-white font-sans font-bold text-sm rounded-xl hover:bg-bloom-green-mid transition-all shadow-lg hover:shadow-bloom-charcoal/10"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
